import './DynamicWrapper.css';
import PropTypes from 'prop-types';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { useHeightAnimation } from './hooks/useHeightAnimation';
import { useFlipAnimation } from './hooks/useFlipAnimation';
import { useSlideAnimation } from './hooks/useSlideAnimation';
import { useStatusTransition } from './hooks/useStatusTransition';
import { useRouteTransition } from './hooks/useRouteTransition';
import { useProgressBusyState } from './hooks/useProgressBusyState';
import { useDeviceDetection } from './hooks/useDeviceDetection';
import ContentSwitcher from './components/ContentSwitcher';
import { hasStatus, isSdkStatus, isFullscreenStatus, isClosingStatus, isGlobalProgressStatus } from '../../constants/status';
import { SDK_HEIGHT } from '../../constants/animations';
import { DisplayLocationContext } from './context/DisplayLocationContext';
import { TransitionStageContext } from './context/TransitionStageContext';

const VERSION = '0.1.58';

/**
 * Componente orquestrador de transições suaves entre rotas e estados de status
 *
 * Este é o componente principal que coordena todos os hooks especializados para criar
 * uma experiência de navegação fluida com animações suaves.
 *
 * Responsabilidades:
 * - Orquestrar 4 hooks especializados (cada um com uma responsabilidade única)
 * - Calcular estado derivado (isShowingGlobalProgress)
 * - Passar props corretas para ContentSwitcher
 * - Fornecer refs para animação de altura
 *
 * Arquitetura:
 * - Não contém lógica de negócio complexa (apenas coordenação)
 * - Toda lógica está extraída em hooks reutilizáveis e testáveis
 * - Fácil de entender: apenas 70 linhas de código limpo
 *
 * Fluxo de dados:
 * 1. useProgressBusyState: gerencia se GlobalProgress está processando fila
 * 2. useStatusTransition: gerencia transições status ↔ conteúdo
 * 3. useRouteTransition: gerencia transições entre rotas
 * 4. useHeightAnimation: anima altura ao trocar conteúdo
 * 5. ContentSwitcher: decide entre GlobalProgress ou Router/children
 *
 * @param {Object} props - Props do componente
 * @param {Object} props.location - Objeto location do react-router com { pathname, search, hash, state, key }
 * @param {Object|null} props.status - Status atual (loading/success/error) com { type: string, text: string } ou null
 * @param {Array<Object>} [props.routes] - Array de rotas com { path: string, element: JSX.Element } (opcional se usar children)
 * @param {React.ReactNode} [props.children] - Conteúdo a ser renderizado (opcional se usar routes)
 * @param {boolean} [props.slideInOnMount] - If true, the wrapper mounts off screen and slides
 *   in once, on the initial mount. Doesn't react to later changes to the prop.
 * @param {boolean} [props.slideInReady] - Gates WHEN the slide-in from slideInOnMount
 *   actually plays, independent of the pin itself — see useSlideAnimation's own doc
 *   for why they're separate signals. Defaults to `true` (reveal as soon as painted),
 *   matching the pre-existing behavior for callers that don't pass it. Pass `false`
 *   if the caller needs to defer the reveal (e.g. until its own content has finished
 *   rendering) without delaying the off-screen pin itself. Not a hard contract — if
 *   it's never flipped back to `true`, useSlideAnimation's own safety timeout reveals
 *   unanimated anyway rather than pinning the wrapper off screen forever.
 * @returns {JSX.Element} Wrapper com altura animada contendo GlobalProgress ou Router/children
 *
 * @example
 * // Com routes
 * <DynamicWrapper
 *   location={location}
 *   status={status}
 *   routes={[
 *     { path: "/", element: <Home /> },
 *     { path: "/about", element: <About /> }
 *   ]}
 * />
 *
 * @example
 * // Com children
 * <DynamicWrapper location={location} status={status}>
 *   <Layout />
 * </DynamicWrapper>
 */
const DynamicWrapper = ({
  location,
  status,
  routes,
  children,
  headerContent,
  pinnedContent,
  pinnedFooter,
  slideInOnMount = false,
  slideInReady = true,
}) => {
  const enableDynamicWrapper = true;

  // Status único sendo exibido
  const [currentStatus, setCurrentStatus] = useState(null);
  const statusStartTimeRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const headerContentRef = useRef(null);
  const previousStatusRef = useRef(status);
  const pendingResetRef = useRef(false); // Rastreia se há um reset pendente
  const pendingStatusRef = useRef(null); // Rastreia se há um status pendente
  const previousHeightRef = useRef(null); // Guarda altura anterior para restaurar após SDK
  // Altura observada do headerContent. Mantida em state para garantir re-render
  // a cada mudança e propagada via CSS variable (--dw-header-height) para que
  // o .dw-modal-content--portal (renderizado em portal, fora desta árvore)
  // consiga se alinhar ao mesmo top do .wrapper.
  const [headerHeight, setHeaderHeight] = useState(0);

  // Gerencia status único: ignora duplicatas de type, aguarda tempo mínimo para trocar
  useLayoutEffect(() => {
    const newStatus = status;
    const hasNewStatus = newStatus && newStatus.type;
    const hasCurrentStatus = currentStatus && currentStatus.type;

    // Caso 1: Novo status chega
    if (hasNewStatus && newStatus !== previousStatusRef.current) {
      previousStatusRef.current = newStatus;

      // Se é o mesmo type do atual, ignora
      if (hasCurrentStatus && newStatus.type === currentStatus.type) {
        return;
      }

      // Se não há status atual, exibe imediatamente
      if (!hasCurrentStatus) {
        setCurrentStatus({ ...newStatus, _timestamp: Date.now() });
        // Se é SDK, define statusStartTimeRef imediatamente (SDK não chama handleTextVisible)
        if (isSdkStatus(newStatus)) {
          statusStartTimeRef.current = Date.now();
        }
        // Para outros status, statusStartTimeRef será definido quando fade-in completar
        return;
      }

      // Status diferente: verifica se já passou tempo mínimo
      // Se statusStartTimeRef ainda é null, significa que o fade-in não completou ainda
      if (!statusStartTimeRef.current) {
        // Salva status pendente para processar quando fade-in completar
        pendingStatusRef.current = newStatus;
        return;
      }

      const elapsedTime = Date.now() - statusStartTimeRef.current;
      const remainingTime = Math.max(0, 1000 - elapsedTime); // MIN_TEXT_DISPLAY

      // Cancela transição pendente se houver
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // Agenda troca após tempo mínimo
      transitionTimeoutRef.current = setTimeout(() => {
        setCurrentStatus({ ...newStatus, _timestamp: Date.now() });
        // Se é SDK, define statusStartTimeRef imediatamente (SDK não chama handleTextVisible)
        if (isSdkStatus(newStatus)) {
          statusStartTimeRef.current = Date.now();
        } else {
          // Para outros status, statusStartTimeRef será definido quando fade-in completar
          statusStartTimeRef.current = null;
        }
        transitionTimeoutRef.current = null;
      }, remainingTime);

      return;
    }

    // Caso 2: Reset (status null)
    if (!newStatus && previousStatusRef.current) {
      previousStatusRef.current = null;

      // Se não há status atual, não faz nada
      if (!hasCurrentStatus) {
        return;
      }

      // Verifica se já passou tempo mínimo
      // Se statusStartTimeRef ainda é null, significa que o fade-in não completou ainda
      if (!statusStartTimeRef.current) {
        // Marca reset como pendente para processar quando fade-in completar
        pendingResetRef.current = true;
        return;
      }

      const elapsedTime = Date.now() - statusStartTimeRef.current;
      const remainingTime = Math.max(0, 1000 - elapsedTime); // MIN_TEXT_DISPLAY

      // Cancela transição pendente se houver
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      // Agenda limpeza após tempo mínimo
      transitionTimeoutRef.current = setTimeout(() => {
        setCurrentStatus(null);
        statusStartTimeRef.current = null;
        transitionTimeoutRef.current = null;
      }, remainingTime);
    }
  }, [status, currentStatus]);

  // Detecta se o dispositivo é desktop
  const isDesktop = useDeviceDetection();

  // Gerencia estado de busy do GlobalProgress
  const { globalProgressBusy, handleBusyChange } = useProgressBusyState(status);

  // Callback quando fade-in do texto completa
  const handleTextVisible = () => {
    statusStartTimeRef.current = Date.now();

    // Se há um status pendente, processa agora
    if (pendingStatusRef.current) {
      const pendingStatus = pendingStatusRef.current;
      pendingStatusRef.current = null;

      // Agenda troca após tempo mínimo (1000ms a partir de agora)
      transitionTimeoutRef.current = setTimeout(() => {
        setCurrentStatus({ ...pendingStatus, _timestamp: Date.now() });
        statusStartTimeRef.current = null; // Reseta para aguardar novo fade-in
        transitionTimeoutRef.current = null;
      }, 1000); // MIN_TEXT_DISPLAY
      return;
    }

    // Se há um reset pendente, processa agora
    if (pendingResetRef.current) {
      pendingResetRef.current = false;

      // Agenda limpeza após tempo mínimo (1000ms a partir de agora)
      transitionTimeoutRef.current = setTimeout(() => {
        setCurrentStatus(null);
        statusStartTimeRef.current = null;
        transitionTimeoutRef.current = null;
      }, 1000); // MIN_TEXT_DISPLAY
    }
  };

  // Gerencia transições de status (loading -> content)
  const {
    displayStatus,
    statusForProgress,
    contentFadeOut,
    contentDelay,
    fromLoading,
  } = useStatusTransition(currentStatus, globalProgressBusy);

  // Gerencia transições entre rotas
  const { displayLocation, transitionStage } = useRouteTransition(location);

  const isFullscreen = isFullscreenStatus(status);
  const isClosing = isClosingStatus(status);

  // Verifica se está exibindo GlobalProgress (exceto SDK e fullscreen)
  const isShowingGlobalProgress =
    isGlobalProgressStatus(displayStatus) || (globalProgressBusy && !isFullscreen);

  // Mirrors ContentSwitcher's own `shouldRenderGlobalProgress` (driven by
  // `statusForProgress`, not `displayStatus`) — this is the true, earliest
  // signal for "GlobalProgress is about to be in the DOM this render",
  // updating synchronously well before `displayStatus`/`isShowingGlobalProgress`
  // do. useHeightAnimation uses it only to freeze the wrapper's CURRENT
  // height immediately (never to measure a new one), preventing a visible
  // auto-size flash during the overlap where ContentSwitcher briefly renders
  // both the outgoing content and GlobalProgress as siblings.
  //
  // Shares `isGlobalProgressStatus` with ContentSwitcher's own
  // `shouldRenderGlobalProgress` check (see constants/status.js) so the two
  // can't silently drift apart — that exact kind of duplication is what
  // caused the auto-grow bug this hook now guards against in the first
  // place (see git history).
  const isRenderingGlobalProgress =
    isGlobalProgressStatus(statusForProgress) || isShowingGlobalProgress;

  // Gerencia animação de altura
  const { wrapperRef, contentRef } = useHeightAnimation(
    displayStatus?.type,
    displayLocation.pathname,
    globalProgressBusy,
    isShowingGlobalProgress,
    status, // Passa status prop para verificar se é SDK
    headerContentRef,
    isRenderingGlobalProgress,
  );

  useFlipAnimation(isFullscreen, wrapperRef, isClosing);

  useSlideAnimation(wrapperRef, { slideInOnMount, slideInReady, isClosing, isDesktop, isFullscreen });

  // Gerencia altura especial para status SDK
  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    // Usa status prop diretamente para responder imediatamente a mudanças
    const isSdk = isSdkStatus(status);

    if (isSdk) {
      // Guarda altura atual antes de aplicar altura SDK
      if (!previousHeightRef.current) {
        const currentHeight = wrapperRef.current.style.height || 'auto';
        previousHeightRef.current = currentHeight;
      }

      // Aplica altura SDK
      wrapperRef.current.style.height = SDK_HEIGHT;
    } else if (
      !isSdk &&
      hasStatus(displayStatus) &&
      !isSdkStatus(displayStatus)
    ) {
      // Status mudou de SDK para loading/success
      // NÃO removemos a altura aqui - deixamos o useHeightAnimation gerenciar
      // Mas limpamos o previousHeightRef para não tentar remover depois
      if (previousHeightRef.current !== null) {
        previousHeightRef.current = null;
      }
    } else if (
      !isSdk &&
      !hasStatus(displayStatus) &&
      previousHeightRef.current !== null
    ) {
      // Status mudou de SDK para null E displayStatus também é null (reset completo)
      // NÃO removemos a altura aqui - deixamos o useHeightAnimation gerenciar
      // A altura será removida naturalmente após a animação
      previousHeightRef.current = null;
    }
    // IMPORTANTE: Quando status muda de SDK para loading/success/null, NÃO removemos a altura do SDK.
    // O useHeightAnimation precisa da altura SDK para animar suavemente.
  }, [status, currentStatus, displayStatus]);

  // Observa o tamanho do headerContent e propaga em duas frentes:
  //   1. state local `headerHeight` → top inline do .wrapper (desktop).
  //   2. CSS variable `--dw-header-height` no <html> → consumida por
  //      .dw-modal-content--portal.wrapper--desktop no Modal.css. Necessário
  //      porque o ModalRenderer monta o modal via portal num container irmão
  //      do .wrapper (vide dw-modal-portal em ModalRenderer.jsx) e portanto
  //      não herda nada da árvore React do DynamicWrapper.
  useLayoutEffect(() => {
    const node = headerContentRef.current;
    if (!node) {
      setHeaderHeight(0);
      document.documentElement.style.setProperty('--dw-header-height', '0px');
      return undefined;
    }

    const measure = () => {
      const child = node.lastElementChild || node;
      const h = child.clientHeight || 0;
      setHeaderHeight(h);
      document.documentElement.style.setProperty('--dw-header-height', `${h}px`);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [headerContent]);

  const content = (
    <ContentSwitcher
      displayStatus={displayStatus}
      globalProgressBusy={isFullscreen ? false : globalProgressBusy}
      statusForProgress={statusForProgress}
      onBusyChange={handleBusyChange}
      onTextVisible={handleTextVisible}
      routes={routes}
      displayLocation={displayLocation}
      transitionStage={transitionStage}
      contentFadeOut={contentFadeOut}
      contentDelay={contentDelay}
      fromLoading={fromLoading}
    >
      {children}
    </ContentSwitcher>
  );

  if (enableDynamicWrapper) {
    return (
      <TransitionStageContext.Provider value={transitionStage}>
        <DisplayLocationContext.Provider value={displayLocation}>
          {
            headerContent && (
              <div ref={headerContentRef}>
                {headerContent}
              </div>
            )
          }
          <div
            className={`wrapper ${isDesktop ? 'wrapper--desktop' : 'wrapper--mobile'}${isFullscreen ? ' wrapper--fullscreen' : ''}${isClosing ? ' wrapper--closing' : ''}`}
            ref={wrapperRef}
            style={!isFullscreen && isDesktop && headerHeight > 0 ? { top: `${headerHeight}px` } : {}}
          >
            <div ref={contentRef}>
              {pinnedContent}
              {content}
              {pinnedFooter}
            </div>
          </div>
        </DisplayLocationContext.Provider>
      </TransitionStageContext.Provider>
    );
  }

  return (
    <TransitionStageContext.Provider value={transitionStage}>
      <DisplayLocationContext.Provider value={displayLocation}>
        {content}
      </DisplayLocationContext.Provider>
    </TransitionStageContext.Provider>
  );
};

DynamicWrapper.propTypes = {
  location: PropTypes.object.isRequired,
  status: PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string,
  }),
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      element: PropTypes.element.isRequired,
    }),
  ).isRequired,
  children: PropTypes.node,
  headerContent: PropTypes.node,
  pinnedContent: PropTypes.node,
  pinnedFooter: PropTypes.node,
  slideInOnMount: PropTypes.bool,
  slideInReady: PropTypes.bool,
};

export default DynamicWrapper;
